import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function WhyChooseUs() {
  const reasons = [
    {
      icon: 'ri-award-line',
      title: 'Trusted Legacy in Cocoa Export',
      description: 'Since 1994, we\'ve built a reputation as a trusted and dependable partner in the global cocoa industry, growing from humble beginnings to exporting over 100,000 metric tonnes of premium Nigerian cocoa annually.',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/f34f47fa8df4ea8b8281ef2fb89b7d66.png'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Direct Access to Origin',
      description: 'Located in the heart of Nigeria\'s rainforest cocoa belt, we operate close to the source, partnering with tens of thousands of farmers to ensure freshness, consistent quality, and responsible, traceable sourcing.',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/d24824dc4376446c706102ff7e02193e.png'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Advanced Export Infrastructure',
      description: 'Our government-approved bonded warehouse and inland dry port enable streamlined container stuffing, inspection, and export logistics, all conducted under strict regulatory oversight. This setup ensures full compliance with international standards while optimizing speed and efficiency across our supply chain.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20bonded%20warehouse%20facility%20with%20inland%20dry%20port%20operations%2C%20large%20industrial%20building%20with%20government%20compliance%20signage%2C%20container%20loading%20operations%2C%20professional%20logistics%20infrastructure%2C%20regulatory%20inspection%20areas%2C%20efficient%20export%20facility%20with%20modern%20equipment%2C%20industrial%20photography%20with%20clear%20professional%20lighting&width=600&height=400&seq=export-infrastructure&orientation=landscape'
    },
    {
      icon: 'ri-route-line',
      title: 'Fully Traceable Supply Chain',
      description: 'We deploy advanced digital tools for farm mapping, farmer registration, and supply chain monitoring, ensuring end-to-end traceability and full compliance with international standards, including the EU Deforestation Regulation (EUDR).',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/a51a8612cd43a8db4d3f07d6bbf49c06.jpeg'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability at the Core',
      description: 'Our sustainability model is built to create lasting impact, supporting farmer livelihoods, child education, climate-smart agriculture, and community development across the cocoa-producing regions we serve.',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/838072bb1cd3483a2a22ecc94bcbb8bd.jpeg'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality You Can Rely On',
      description: 'From sourcing to shipment, we implement rigorous quality control protocols at every stage to ensure consistent, export-grade cocoa beans that meet the exacting specifications of our global clients.',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/78d59538a77af81e77417226355a7ccf.png'
    }
  ];

  const statistics = [
    { number: '30+', label: 'Years of Excellence', icon: 'ri-calendar-line' },
    { number: '100,000+', label: 'MT Annual Export', icon: 'ri-truck-line' },
    { number: '10,000+', label: 'Partner Farmers', icon: 'ri-group-line' },
    { number: '100%', label: 'Traceable Supply', icon: 'ri-shield-check-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://readdy.ai/api/search-image?query=Premium%20Nigerian%20cocoa%20beans%20showcase%2C%20high-quality%20cocoa%20beans%20with%20rich%20brown%20color%20and%20perfect%20texture%2C%20professional%20product%20photography%20highlighting%20excellence%20and%20quality%2C%20warm%20golden%20lighting%2C%20traditional%20African%20patterns%20subtly%20integrated%2C%20luxury%20agricultural%20product%20presentation&width=1920&height=800&seq=why-choose-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Why Choose Us
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-5xl mx-auto leading-relaxed">
            At Olatunde International Limited, we blend tradition, innovation, and integrity 
            to deliver cocoa that consistently meets, and exceeds the world's highest standards.
          </p>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {statistics.map((stat, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-2xl text-amber-600`}></i>
                </div>
                <h3 className="text-3xl font-bold text-green-800 mb-2">{stat.number}</h3>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-24">
            {reasons.map((reason, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                    <i className={`${reason.icon} text-2xl text-amber-600`}></i>
                  </div>
                  <h2 className="text-3xl font-bold text-green-800 mb-6">{reason.title}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">{reason.description}</p>
                  
                  <div className="flex items-center space-x-4">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <span className="text-amber-600 font-medium">Excellence Since 1994</span>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img 
                      src={reason.image} 
                      alt={reason.title}
                      className="w-full h-96 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-green-800 mb-8">
              Our Commitment to Excellence
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-4">Tradition</h3>
                <p className="text-gray-600 leading-relaxed">
                  Three decades of expertise in Nigerian cocoa export, honoring traditional methods while embracing innovation.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-4">Innovation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Cutting-edge technology for traceability, quality control, and sustainable farming practices.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-star-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-4">Integrity</h3>
                <p className="text-gray-600 leading-relaxed">
                  Transparent business practices, ethical sourcing, and unwavering commitment to quality standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.9), rgba(34, 197, 94, 0.9)), url('https://readdy.ai/api/search-image?query=Handshake%20between%20Nigerian%20cocoa%20farmer%20and%20international%20business%20partner%2C%20professional%20partnership%20meeting%20in%20cocoa%20plantation%20setting%2C%20successful%20business%20collaboration%2C%20trust%20and%20reliability%20in%20agricultural%20trade%2C%20warm%20professional%20lighting%2C%20partnership%20and%20cooperation%20theme&width=1920&height=600&seq=partnership-cta&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Experience the Olatunde Difference
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Join the ranks of satisfied global clients who trust us for premium Nigerian cocoa. 
            Let us show you why we're the preferred partner for quality, reliability, and sustainability.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/services">
              <Button variant="primary" size="lg" className="bg-amber-600 hover:bg-amber-700">
                Explore Our Services
                <div className="w-5 h-5 flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-green-800">
              Contact Our Team
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-phone-line"></i>
              </div>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
